import { create } from "zustand";
import { persist } from "zustand/middleware";

export type EditorWidth = "narrow" | "wide" | "full";

/** The editor pane should belong to the notes: "full" (default) uses the
 *  available pane width minus tasteful margins, scaling with sidebars and
 *  display size instead of a fixed column. */
const WIDTH_CSS: Record<EditorWidth, string> = {
  narrow: "46em",
  wide: "78em",
  full: "min(140em, calc(100% - clamp(48px, 6%, 160px)))",
};

interface SettingsState {
  editorFontSize: number; // px
  editorWidth: EditorWidth;
  /** Confetti + task animations. A manual toggle only, on by default. */
  celebrationsEnabled: boolean;
  setEditorFontSize: (v: number) => void;
  setEditorWidth: (v: EditorWidth) => void;
  setCelebrationsEnabled: (v: boolean) => void;
}

export const useSettings = create<SettingsState>()(
  persist(
    (set) => ({
      editorFontSize: 15,
      editorWidth: "full",
      celebrationsEnabled: true,
      setEditorFontSize: (v) => set({ editorFontSize: Math.min(22, Math.max(12, v)) }),
      setEditorWidth: (v) => set({ editorWidth: v }),
      setCelebrationsEnabled: (v) => set({ celebrationsEnabled: v }),
    }),
    {
      name: "hn.settings",
      version: 3,
      migrate: (state) => {
        // v1 had editorLineWidth (number em); map old custom widths sensibly
        const s = state as {
          editorLineWidth?: number;
          editorWidth?: EditorWidth;
          editorFontSize?: number;
          celebrationsEnabled?: boolean;
        };
        if (!s.editorWidth) {
          s.editorWidth = s.editorLineWidth && s.editorLineWidth <= 50 ? "narrow" : "full";
        }
        if (s.celebrationsEnabled === undefined) s.celebrationsEnabled = true;
        return s as SettingsState;
      },
    },
  ),
);

/** Push settings into CSS variables; call once at boot and on change. */
export function applySettingsToCss(): () => void {
  const apply = (s: SettingsState) => {
    const root = document.documentElement;
    root.style.setProperty("--fs-editor", `${s.editorFontSize}px`);
    root.style.setProperty("--editor-measure", WIDTH_CSS[s.editorWidth] ?? WIDTH_CSS.full);
    if (s.celebrationsEnabled) root.removeAttribute("data-celebrations");
    else root.setAttribute("data-celebrations", "off");
  };
  apply(useSettings.getState());
  return useSettings.subscribe(apply);
}
